import {
  MutationKey,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { portalApiClient } from "../apiClient";
import { apiUrls } from "src/consts/ApiUrls";
import { useEffect, useMemo } from "react";
import useMyUser from "./useMyUser";
import { FileType, SasFileResult } from "src/types/InspectionUpdateRequest";
import { useFilesForCompanyId } from "./useFiles";

type StartSigningSessionResponse = {
  id: number;
  documentId: number;
  nonce: string;
  signingStatus: number | string;
  expiresAt: string;
};

export function useSigningSession() {
  const { companyId, documentId, signingSessionId } = useParams();
  const { data: myUser } = useMyUser();
  const startSigningSessionPayload = useMemo(
    () => ({
      documentId: Number(documentId),
      userId: Number(myUser?.id),
      companyId: Number(companyId),
    }),
    [documentId, myUser?.id, companyId]
  );
  const queryKeyBase = useMemo(
    () => ["SigningSession", companyId],
    [companyId]
  );

  const startSigningSession = useQuery({
    queryKey: ["Start", ...queryKeyBase, documentId],
    queryFn: () =>
      portalApiClient
        .post(apiUrls.SigningSession.start(), startSigningSessionPayload)
        .then((d) => {

          return d.data as StartSigningSessionResponse;
        }),
    enabled: !!documentId,
    refetchOnMount: true,
  });

  const signDocument = useMutation({
    mutationFn: (variables: {
      payload: { nonce: string; signatureBase64: string };
      signingSessionId: number;
    }) =>
      portalApiClient
        .post(
          apiUrls.SigningSession.sign(variables.signingSessionId),
          variables.payload
        )
        .then((d) => d.data),
    mutationKey: [...queryKeyBase, documentId, signingSessionId, "Sign"],
  });

  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: queryKeyBase });
  }, [signDocument.isSuccess]);
  const files = useFilesForCompanyId();
  return {
    get: files,
    start: startSigningSession,
    sign: signDocument,
  };
}
